import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button.js';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../features/auth/AuthContext.js';
import type { Language } from '@lifetimeline/shared';

import { useI18n } from '../i18n/index.js';
import { ENGLISH_NAME } from '../lib/branding.js';
import styles from './LandingPage.module.css';

/**
 * 미리보기 격자에 쓸 칸. 사진 없이 **색과 글자만**으로 서비스의 모양을 보여준다.
 *
 * **언어마다 따로 쓴다.** 영어로 보는 사람에게 `진격의 거인`이 늘어서 있으면
 * 서비스의 모양이 아니라 한국어 목록으로 읽힌다. 카테고리 이름은 실제
 * 카테고리와 같은 말로 맞췄다(`키보드`/`Keyboard`) — 랜딩에서 본 말을
 * 검색창에 그대로 쳤을 때 나와야 한다.
 *
 * 문구지만 `i18n/`이 아니라 여기 둔다. 칸의 색(`tone`)과 짝을 이루는 데이터라
 * 떼어 놓으면 여섯 줄을 두 파일에서 맞춰 봐야 한다.
 */
/**
 * 미리보기 — **타임라인 모양**을 보여준다.
 *
 * 이 서비스가 뭔지는 "연도 축에 사건이 줄지어 선 모습"을 한 번 보면 끝나는데,
 * 글로 풀면 세 문단이 든다.
 *
 * ## 무엇을 적어 두는가
 *
 * **인생이지 이력서가 아니다.** 예전에는 창업·팀 리드·첫 회사·처음 코드를
 * 썼다로 채워 두었는데, 다섯 줄이 전부 한 사람의 경력이라 "개발자 이력
 * 정리하는 곳"으로 읽혔다. 여기 걸린 것이 곧 이 서비스가 무엇을 담는
 * 곳인지의 정의다 — 일도 있고 여행도 있고 배움도 있어야 한다.
 *
 * **제목은 일어난 일, 덧붙임은 곁들이는 말.** 예전에는 `첫 출근`이 제목
 * 자리에 있었는데, 그건 입사라는 일에 따라붙는 장면이지 그 해에 일어난
 * 일 자체가 아니다. 제목은 `OOO 입사`, 덧붙임이 `첫 출근`이다.
 * 회사 이름은 `OOO`로 둔다 — 첫 화면에 실재하는 회사를 박아 두면 그
 * 회사와 무슨 관계가 있는 것처럼 읽힌다.
 *
 * **실제 사람의 기록에서 뼈대를 땄다**(만든 사람의 프로필). 지어낸 인생은
 * 고르기 쉬운 것만 남아 밋밋하다 — 운전면허, 졸업, 첫 출근, 이직, 휴가
 * 여행처럼 결이 다른 일이 섞여야 "인생"으로 읽힌다. 색도 셋을
 * 다 쓴다: 일(파랑)·배움(주황)·여행(초록).
 *
 * ⚠️ 줄마다 **연도가 달라야 한다** — 연도를 줄의 key로 쓴다.
 *
 * 덧붙임은 **두 줄만** 채운다. 다 채우면 한 줄짜리 기록도 된다는 게 안 보이고,
 * 축이 빽빽해져서 연도가 안 읽힌다.
 *
 * **같은 사람의 같은 이야기를 언어만 바꿔** 쓴다. 한국에 사는 한 사람이다 —
 * 언어마다 다른 사람을 지어내면 어느 쪽이 "이 서비스의 모습"인지 흐려진다.
 * 영어로 보는 사람에게 한국어 사건이 늘어서 있으면 남의 기록으로 읽히니
 * 글자는 옮기되, 이야기는 그대로다.
 *
 * 여행은 **어느 나라 사람이 봐도 아는 곳**으로 고른다(삿포로).
 * 예전의 `제주 한 달 살기`는 해외에서는 어딘지부터 설명해야 했다.
 *
 * ⚠️ 영어판에는 `OOO`를 안 쓴다. 영어권에서는 "Out Of Office(부재중)"로
 * 읽힌다. 회사 이름을 가리는 대신 문장에서 뺀다 — 같은 일, 그 말의 관례대로.
 */
const PREVIEW: Record<
  Language,
  readonly { year: number; title: string; note: string; tone: string }[]
> = {
  ko: [
    { year: 2024, title: '삿포로 스노보드 여행', note: '첫 파우더', tone: 'c' },
    { year: 2022, title: 'OOO 이직', note: '', tone: 'a' },
    { year: 2019, title: 'OOO 입사', note: '첫 출근', tone: 'a' },
    { year: 2018, title: '고등학교 졸업', note: '', tone: 'b' },
    { year: 2017, title: '운전면허 취득', note: '', tone: 'b' },
  ],
  en: [
    { year: 2024, title: 'Snowboarding in Sapporo', note: 'First powder day', tone: 'c' },
    { year: 2022, title: 'Changed jobs', note: '', tone: 'a' },
    { year: 2019, title: 'Joined my first company', note: 'First day at work', tone: 'a' },
    { year: 2018, title: 'Graduated high school', note: '', tone: 'b' },
    { year: 2017, title: "Got my driver's license", note: '', tone: 'b' },
  ],
  ja: [
    { year: 2024, title: '札幌でスノーボード', note: '初めてのパウダー', tone: 'c' },
    { year: 2022, title: '転職', note: '', tone: 'a' },
    { year: 2019, title: '新卒で入社', note: '初出勤', tone: 'a' },
    { year: 2018, title: '高校卒業', note: '', tone: 'b' },
    { year: 2017, title: '運転免許を取得', note: '', tone: 'b' },
  ],
  'zh-cn': [
    { year: 2024, title: '札幌单板滑雪之旅', note: '第一次滑粉雪', tone: 'c' },
    { year: 2022, title: '换了工作', note: '', tone: 'a' },
    { year: 2019, title: '加入第一家公司', note: '第一天上班', tone: 'a' },
    { year: 2018, title: '高中毕业', note: '', tone: 'b' },
    { year: 2017, title: '考到驾照', note: '', tone: 'b' },
  ],
  'zh-tw': [
    { year: 2024, title: '札幌滑雪板之旅', note: '第一次滑粉雪', tone: 'c' },
    { year: 2022, title: '換工作', note: '', tone: 'a' },
    { year: 2019, title: '進入第一間公司', note: '第一天上班', tone: 'a' },
    { year: 2018, title: '高中畢業', note: '', tone: 'b' },
    { year: 2017, title: '考到駕照', note: '', tone: 'b' },
  ],
  es: [
    { year: 2024, title: 'Snowboard en Sapporo', note: 'Mi primer día de nieve polvo', tone: 'c' },
    { year: 2022, title: 'Cambié de trabajo', note: '', tone: 'a' },
    { year: 2019, title: 'Entré en mi primera empresa', note: 'Primer día de trabajo', tone: 'a' },
    { year: 2018, title: 'Terminé el bachillerato', note: '', tone: 'b' },
    { year: 2017, title: 'Obtuve mi licencia de conducir', note: '', tone: 'b' },
  ],
  pt: [
    { year: 2024, title: 'Snowboard em Sapporo', note: 'Primeiro dia de neve fresca', tone: 'c' },
    { year: 2022, title: 'Mudei de emprego', note: '', tone: 'a' },
    {
      year: 2019,
      title: 'Entrei no meu primeiro emprego',
      note: 'Primeiro dia de trabalho',
      tone: 'a',
    },
    { year: 2018, title: 'Terminei o ensino médio', note: '', tone: 'b' },
    { year: 2017, title: 'Tirei a carteira de motorista', note: '', tone: 'b' },
  ],
};

/**
 * 미리보기 — **취향 격자**. 타임라인 아래에 붙는다.
 *
 * 이 서비스는 한 화면에 둘이다 — 인생(타임라인)과 취향(픽). 타임라인만
 * 보여주면 들어온 사람이 나머지 절반을 모른 채 지나간다. "한 장에"라고
 * 써 놓고 한 가지만 보여주면 그 말이 빈말이 된다.
 *
 * 사진을 쓰지 않는다. 남의 사진을 첫 화면에 박아 두면 출처를 책임져야 하고
 * 용량도 는다 — 색 칸만으로도 "칸마다 하나씩"은 전달된다.
 *
 * 카테고리 이름은 **실제 카테고리와 같은 말**로 맞춘다. 랜딩에서 본 말을
 * 검색창에 그대로 쳤을 때 나와야 한다.
 */
const PICKS: Record<Language, readonly { category: string; item: string; tone: string }[]> = {
  ko: [
    { category: '동물', item: '개', tone: 'a' },
    { category: '운동', item: '크로스핏', tone: 'b' },
    { category: '영화', item: '인터스텔라', tone: 'c' },
    { category: '키보드', item: '리얼포스', tone: 'd' },
    { category: '애니메이션', item: '진격의 거인', tone: 'e' },
    { category: '게임', item: '전략적 팀 전투', tone: 'f' },
  ],
  /*
   * 타임라인과 같은 사람이다 — 한국어판과 **같은 것**을 그 말로 적는다.
   * 칸마다 **어느 나라 사람이 봐도 아는 것**이어야 한다. 웹툰을 뺀 이유다 —
   * 국내 작품이라 해외에서는 칸 하나가 통째로 뜻 모를 줄이 됐다.
   */
  en: [
    { category: 'Animal', item: 'Dog', tone: 'a' },
    { category: 'Sport', item: 'CrossFit', tone: 'b' },
    { category: 'Movie', item: 'Interstellar', tone: 'c' },
    { category: 'Keyboard', item: 'REALFORCE', tone: 'd' },
    { category: 'Animation', item: 'Attack on Titan', tone: 'e' },
    { category: 'Game', item: 'Teamfight Tactics', tone: 'f' },
  ],
  ja: [
    { category: '動物', item: '犬', tone: 'a' },
    { category: 'スポーツ', item: 'クロスフィット', tone: 'b' },
    { category: '映画', item: 'インターステラー', tone: 'c' },
    { category: 'キーボード', item: 'REALFORCE', tone: 'd' },
    { category: 'アニメ', item: '進撃の巨人', tone: 'e' },
    { category: 'ゲーム', item: 'チームファイト タクティクス', tone: 'f' },
  ],
  'zh-cn': [
    { category: '动物', item: '狗', tone: 'a' },
    { category: '运动', item: 'CrossFit', tone: 'b' },
    { category: '电影', item: '星际穿越', tone: 'c' },
    { category: '键盘', item: 'REALFORCE', tone: 'd' },
    { category: '动画', item: '进击的巨人', tone: 'e' },
    { category: '游戏', item: '云顶之弈', tone: 'f' },
  ],
  'zh-tw': [
    { category: '動物', item: '狗', tone: 'a' },
    { category: '運動', item: 'CrossFit', tone: 'b' },
    { category: '電影', item: '星際效應', tone: 'c' },
    { category: '鍵盤', item: 'REALFORCE', tone: 'd' },
    { category: '動畫', item: '進擊的巨人', tone: 'e' },
    { category: '遊戲', item: '聯盟戰棋', tone: 'f' },
  ],
  es: [
    { category: 'Animal', item: 'Perro', tone: 'a' },
    { category: 'Deporte', item: 'CrossFit', tone: 'b' },
    { category: 'Película', item: 'Interstellar', tone: 'c' },
    { category: 'Teclado', item: 'REALFORCE', tone: 'd' },
    { category: 'Animación', item: 'Ataque a los Titanes', tone: 'e' },
    { category: 'Juego', item: 'Teamfight Tactics', tone: 'f' },
  ],
  pt: [
    { category: 'Animal', item: 'Cachorro', tone: 'a' },
    { category: 'Esporte', item: 'CrossFit', tone: 'b' },
    { category: 'Filme', item: 'Interstellar', tone: 'c' },
    { category: 'Teclado', item: 'REALFORCE', tone: 'd' },
    { category: 'Animação', item: 'Attack on Titan', tone: 'e' },
    { category: 'Jogo', item: 'Teamfight Tactics', tone: 'f' },
  ],
};

/**
 * 이름의 출처. `라`이프 `타`임`라`인 → 라타라.
 *
 * **두 언어에서 같은 것을 보여준다.** 이 말놀이는 "Life Timeline"을 한글로
 * 옮겼을 때만 성립해서, 영어로 옮길 수가 없다(`LATARA`를 `Life Timeline`에서
 * 딸 수는 없다). 그렇다고 없는 말놀이를 지어 붙이면 뜻 모를 줄이 하나 늘 뿐이고,
 * 안 그리면 영어로 열었을 때 이름이 어디서 왔는지 볼 데가 없어진다.
 *
 * 그래서 **글자 그대로** 보여준다 — 머리에 `LATARA`가 있고 여기 밝은 글자가
 * 셋이라, 한글을 못 읽어도 "세 덩이가 이름이 됐다"는 것은 읽힌다.
 * 한글은 한 글자가 한 음절이라 이 대응이 눈으로 맞는다.
 *
 * ⚠️ 글자를 더 쓰면 **폰트도 다시 잘라야 한다** — `public/fonts/README.md`.
 * 지금 잘라 둔 글자는 `라 이 프 타 임 인`뿐이다.
 */
/**
 * 영어 이름에서 밝히는 글자.
 *
 * ⚠️ **`L T L`이지 `L T R`이 아니다.** `라타라`를 로마자로 적으면
 * `LA TA RA`라 R이 보이는데, 그 `RA`는 `라인`의 `라`다. 영어 단어는
 * `Line`이라 L로 시작하고, `Life TimeLine`에는 R이라는 글자가 아예 없다.
 *
 * 그래서 밝힌 셋이 `LATARA`를 **철자로 만들어 주지는 않는다.** 세 덩이가
 * 이름이 됐다는 것만 눈으로 보인다 — 한국어 줄에서 `라·타·라`가 하는 일의
 * 절반쯤이다.
 *
 * 글자를 손으로 적지 않고 `ENGLISH_NAME`에서 **뽑는다.** 따로 적어 두면
 * 이름을 고칠 때 한쪽만 바뀌어, 밝힌 글자가 이름에 없는 글자가 된다.
 * 대문자가 곧 덩이의 머리글자라는 규칙 하나로 충분하다.
 */
const ORIGIN_EN: readonly { text: string; mark: boolean }[] = Array.from(ENGLISH_NAME).map(
  (letter) => ({ text: letter, mark: /[A-Z]/.test(letter) }),
);

const ORIGIN: readonly { text: string; mark: boolean }[] = [
  { text: '라', mark: true },
  { text: '이프 ', mark: false },
  { text: '타', mark: true },
  { text: '임', mark: false },
  { text: '라', mark: true },
  { text: '인', mark: false },
];

/**
 * 진입 화면.
 *
 * 설명보다 **모양을 먼저 보여준다.** 이 서비스가 뭔지는 "연도 축에 사건이
 * 줄지어 선 모습"을 한 번 보면 끝나는데, 글로 풀면 세 문단이 든다.
 *
 * 미리보기에 진짜 사람의 기록을 쓰지 않는다 — 남의 인생을 랜딩에 박아두면
 * 출처를 책임져야 한다. 있을 법한 몇 줄로도 "연도에 걸린다"는 전달된다.
 */
export function LandingPage() {
  const { t, language } = useI18n();
  const { status } = useAuth();
  const navigate = useNavigate();

  const start = () => {
    /*
     * 비로그인은 **로그인으로** 보낸다.
     *
     * oneby.me에서는 계정 없이 픽을 담아 보고 나중에 로그인하는 동선이었다.
     * 여기서는 그게 성립하지 않는다 — 타임라인의 값은 "정한 사람에게 보여주는
     * 것"인데, 계정이 없으면 주소도 팔로우도 없어서 만들어 봐야 혼자 보는
     * 기록이 된다. 담아 두는 곳이 `/anonymous`(픽 목록)라 본체도 아니었다.
     */
    navigate(status === 'authenticated' ? '/me' : '/signin');
  };

  return (
    <Layout>
      <section className={styles.hero}>
        {/*
          이름이 놓이는 자리.

          한국어에서는 **어디서 왔는지**를 보여준다. 글로 설명하지 않는다 —
          세 글자만 밝혀 두면 `라이프 타임라인`에서 `라타라`가 읽힌다.
          "앞 글자를 따서…"라고 적는 순간 재미가 없어지고 한 줄이 늘어난다.

          영어에서는 그 말놀이가 **성립하지 않는다**(`LATARA`를
          `Life TimeLine`에서 딸 수 없다). 한글을 그대로 두면 못 읽는 기호
          한 줄이 될 뿐이라, 같은 자리에 **영어 이름**을 놓는다. 말놀이 대신
          "무엇을 하는 곳인가"가 읽혀 자리값을 한다.
        */}
        {language === 'ko' ? (
          <p className={styles.origin} lang="ko">
            {ORIGIN.map((part, index) => (
              <span key={index} className={part.mark ? styles.originMark : undefined}>
                {part.text}
              </span>
            ))}
          </p>
        ) : (
          <p className={`${styles.origin} ${styles.originEn}`}>
            {ORIGIN_EN.map((part, index) => (
              <span key={index} className={part.mark ? styles.originMark : undefined}>
                {part.text}
              </span>
            ))}
          </p>
        )}
        <h1 className={styles.title}>{t('landing.title')}</h1>
      </section>

      {/*
        누르는 자리가 **문구 바로 밑**이다. 미리보기 뒤에 두었더니 격자
        두 줄을 스크롤해야 버튼이 나왔다 — 이미 마음을 정하고 들어온 사람이
        제일 먼저 하는 일을 제일 나중에 찾게 만드는 꼴이다.
        미리보기는 아직 안 정한 사람을 위해 아래에 남는다.
      */}
      <div className={styles.actions}>
        {/*
          ⚠️ 폭을 다 쓰지 않는다. 화면 너비만 한 큰 판때기를 붙여 놨더니
          첫 화면에서 제일 무거운 것이 문구가 아니라 버튼이었다 — 읽으러
          온 사람에게 제일 먼저 보이는 게 "눌러라"가 된다.
          글자만큼만 차지하고 문구와 같은 선에서 시작한다.
        */}
        <Button onClick={start}>{t('landing.start')}</Button>
      </div>

      <ol className={styles.preview} aria-hidden="true">
        {PREVIEW[language].map((cell) => (
          <li key={cell.year} className={styles.cell}>
            <span className={styles.cellYear}>{cell.year}</span>
            <span className={styles.cellDot} data-tone={cell.tone} />
            <span className={styles.cellText}>
              <strong className={styles.cellTitle}>{cell.title}</strong>
              {cell.note === '' ? null : <span className={styles.cellNote}>{cell.note}</span>}
            </span>
          </li>
        ))}
      </ol>

      <ul className={styles.picks} aria-hidden="true">
        {PICKS[language].map((tile) => (
          <li key={tile.category} className={styles.tile} data-tone={tile.tone}>
            {/*
              실물 격자(`PickGrid`)의 사진 있는 칸과 **같은 구조**다 —
              칸 아래에 어두운 스크림을 깔고 그 위에 카테고리·이름 두 줄.
              색 칸은 사진 자리를 대신한다.
            */}
            <span className={styles.tileCaption}>
              <span className={styles.tileCategory}>{tile.category}</span>
              <span className={styles.tileItem}>{tile.item}</span>
            </span>
          </li>
        ))}
      </ul>
    </Layout>
  );
}
